/**
 * Notion-style tag chips (SPEC `saved-posts` § Save popup).
 *
 * The value is a list of **names**, not ids: #64 resolves them NOCASE and creates what it does not
 * know, which is what makes "type a word, press Enter" one round trip instead of two. A chip that
 * matches the existing vocabulary renders in its server-assigned colour; a pending new one renders
 * neutral, so "this will be created" is visible before the batch commits.
 *
 * The menu has two modes over one code path. Typing narrows the vocabulary to a capped typeahead;
 * focusing the field with an empty draft **browses** it instead — the whole vocabulary minus what is
 * already chosen, scrolling inside a capped panel. Without that, a tag you created weeks ago is only
 * reachable by remembering how it was spelled.
 *
 * Keyboard: Enter or comma commits the draft, Backspace on an empty draft removes the last chip,
 * ArrowUp/ArrowDown walk the options and Escape closes the menu (never the dialog behind it).
 */

import { useMemo, useRef, useState, type KeyboardEvent } from 'react';
import { SAVED_TAGS_PER_POST_MAX, type SavedTagDTO } from '@lw/shared';
import { CloseIcon, cx } from '../../components';

export type TagInputProps = {
  /** Tag names, in the order the user added them. */
  value: readonly string[];
  onChange: (next: string[]) => void;
  /** The whole vocabulary (#60) — drives both the typeahead and the chip colours. */
  vocabulary: readonly SavedTagDTO[];
  disabled?: boolean;
};

const SUGGESTION_LIMIT = 6;

export function TagInput({ value, onChange, vocabulary, disabled = false }: TagInputProps) {
  const [draft, setDraft] = useState('');
  const [active, setActive] = useState(0);
  // Browsing is a *focus* state, not an open/closed one: the menu belongs to the field, so leaving
  // the field closes it and Escape re-arms it by refocusing.
  const [browsing, setBrowsing] = useState(false);
  const inputRef = useRef<HTMLInputElement | null>(null);

  const byName = useMemo(() => {
    const map = new Map<string, SavedTagDTO>();
    for (const tag of vocabulary) map.set(tag.name.toLowerCase(), tag);
    return map;
  }, [vocabulary]);

  const chosen = useMemo(() => new Set(value.map((name) => name.toLowerCase())), [value]);
  const needle = draft.trim().toLowerCase();
  // One list, two modes. Browsing is uncapped (the panel scrolls) because a capped browse would be
  // an arbitrary window over a vocabulary the user is trying to *see*; the typeahead stays capped
  // because it is a shortcut, not a listing.
  const suggestions = useMemo(() => {
    const available = vocabulary.filter((tag) => !chosen.has(tag.name.toLowerCase()));
    if (needle.length === 0) return browsing ? available : [];
    return available
      .filter((tag) => tag.name.toLowerCase().includes(needle))
      .slice(0, SUGGESTION_LIMIT);
  }, [vocabulary, needle, chosen, browsing]);

  const full = value.length >= SAVED_TAGS_PER_POST_MAX;

  const add = (name: string) => {
    const trimmed = name.trim();
    if (trimmed.length === 0 || full) return;
    // The vocabulary is case-insensitive, so re-typing an existing tag in another case is the same
    // tag; keep the spelling already on screen rather than adding a near-duplicate chip.
    if (chosen.has(trimmed.toLowerCase())) {
      setDraft('');
      return;
    }
    onChange([...value, byName.get(trimmed.toLowerCase())?.name ?? trimmed]);
    setDraft('');
    setActive(0);
  };

  const removeAt = (index: number) => onChange(value.filter((_, position) => position !== index));

  const onKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter' || event.key === ',') {
      event.preventDefault();
      const suggestion = suggestions[active];
      add(suggestion === undefined ? draft : suggestion.name);
      return;
    }
    if (event.key === 'Backspace' && draft.length === 0 && value.length > 0) {
      event.preventDefault();
      removeAt(value.length - 1);
      return;
    }
    if (event.key === 'ArrowDown' && suggestions.length > 0) {
      event.preventDefault();
      setActive((current) => (current + 1) % suggestions.length);
      return;
    }
    if (event.key === 'ArrowUp' && suggestions.length > 0) {
      event.preventDefault();
      setActive((current) => (current - 1 + suggestions.length) % suggestions.length);
      return;
    }
    if (event.key === 'Escape' && (draft.length > 0 || suggestions.length > 0)) {
      // Swallowed deliberately: clearing the draft or closing the menu must not also close the
      // dialog behind it.
      event.stopPropagation();
      setDraft('');
      setBrowsing(false);
      setActive(0);
    }
  };

  return (
    <div className="tag-input">
      <div
        className={cx('tag-input__field', disabled && 'is-disabled')}
        onClick={() => inputRef.current?.focus()}
      >
        {value.map((name, index) => {
          const known = byName.get(name.toLowerCase());
          return (
            <span
              key={`${name}-${String(index)}`}
              className={cx('tag-chip', known === undefined && 'tag-chip--new')}
              data-color={known?.color}
            >
              {name}
              <button
                type="button"
                className="tag-chip__x"
                aria-label={`Remove tag ${name}`}
                disabled={disabled}
                onClick={(event) => {
                  event.stopPropagation();
                  removeAt(index);
                }}
              >
                <CloseIcon size={9} />
              </button>
            </span>
          );
        })}
        <input
          ref={inputRef}
          className="tag-input__entry"
          type="text"
          value={draft}
          disabled={disabled || full}
          placeholder={value.length === 0 ? 'Add tags…' : ''}
          aria-label="Add a tag"
          onFocus={() => setBrowsing(true)}
          onBlur={() => setBrowsing(false)}
          onChange={(event) => {
            setDraft(event.target.value);
            setActive(0);
          }}
          onKeyDown={onKeyDown}
        />
      </div>

      {suggestions.length > 0 ? (
        // `onMouseDown` is what actually blurs the input, and blur unmounts this list — so the
        // default is suppressed here and the click below is allowed to land.
        <ul className="tag-input__menu" onMouseDown={(event) => event.preventDefault()}>
          {suggestions.map((tag, index) => (
            <li key={tag.id}>
              <button
                type="button"
                className={cx('tag-input__option', index === active && 'is-active')}
                onMouseEnter={() => setActive(index)}
                onClick={() => add(tag.name)}
              >
                <span className="tag-chip" data-color={tag.color}>
                  {tag.name}
                </span>
                <span className="faint">{tag.postCount}</span>
              </button>
            </li>
          ))}
        </ul>
      ) : null}

      {full ? (
        <p className="field__hint">
          That is the maximum of {SAVED_TAGS_PER_POST_MAX} tags on one post.
        </p>
      ) : null}
    </div>
  );
}

export default TagInput;
